import { Dispatch, SetStateAction, useRef, useState } from "react";
import { NavigateFunction, useNavigate } from "react-router-dom";
import { Popover, Whisper, WhisperInstance } from "rsuite";
import { useAtomValue } from "jotai";
import { Button } from "reactstrap";

import UppyFile, { UppyFileInterface } from "../../Inputs/UppyFile";
import { SUPPORTED_EXTENSIONS } from "../../../utils/Constants";
import { getErrorMessage } from "../../../utils/GeneralUtils";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import { AppConfirm } from "../../../utils/PopupUtil";
import TenantAPI from "../../../api/api/TenantAPI";
import StrUtils from "../../../utils/StrUtils";
import { CustomFile } from "./PageViewBrieGPT";

interface Props {
  files: CustomFile[];
  setFiles: Dispatch<SetStateAction<CustomFile[]>>;
}

export default function BrieGPTUploadButton({ files, setFiles }: Props) {
  const tenantID = useAtomValue(tenantIDAtom);
  const navigate = useNavigate();

  const [keepOpen, setKeepOpen] = useState(false);
  const whisperRef = useRef<WhisperInstance>(null);

  return (
    <div className="tw:flex">
      <Whisper
        placement="autoVertical"
        preventOverflow
        trigger={keepOpen ? "none" : "click"}
        ref={whisperRef}
        speaker={
          <Popover
            className={`sidebar-popover tw:pb-4 ${keepOpen ? "tw:hidden" : ""}`}
          >
            <div className="tw:flex tw:justify-between tw:items-center tw:border-b tw:border-superlight tw:-mx-2 tw:pb-1">
              <div className="tw:px-4">
                <h5 className="text-dark m-0 pr-2">Add Files To Chat</h5>
              </div>
              <div className="tw:px-2">
                <Button
                  size="sm"
                  outline
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    whisperRef.current?.close();
                  }}
                >
                  Done
                </Button>
              </div>
            </div>
            <UppyFile
              height={208}
              // @ts-ignore
              afterSubmit={(newFiles: UppyFileInterface[]) => {
                whisperRef.current?.close();

                const FILE_SIZE_LIMIT = 60 * 1024 * 1024; // 60 MB in bytes

                for (const newFile of newFiles) {
                  // Check if file exceeds the size limit
                  if (newFile.Size > FILE_SIZE_LIMIT) {
                    return newToastEvent(
                      "error",
                      `One or more files exceed the 60MB limit.`
                    );
                  }
                }

                const finalFileArr: CustomFile[] = [];

                for (const newFileData of newFiles) {
                  const newFile: any = {
                    name: newFileData.Name,
                    size: newFileData.Size,
                    type: newFileData.Type,
                  };

                  if (
                    files.find(
                      (f) =>
                        f.file.name === newFile.name &&
                        f.file.size === newFile.size
                    )
                  ) {
                    continue;
                  }

                  finalFileArr.push({
                    file: newFile,
                    uploading: !newFileData?.Index && !newFileData?.Transcript,
                    uploadID: StrUtils.uuidv4(),
                    uploadedFileData: newFileData,
                  });
                }

                setFiles((prev) => [...prev, ...finalFileArr]);

                // Index / Transcribe files that were marked as "uploading" (They didn't have Index/Transcript property.)
                for (const file of finalFileArr) {
                  if (!file.uploading) continue;

                  TenantAPI.indexFile({
                    fileRecordID: file.uploadedFileData.RecordID!,
                    recordID: undefined,
                    fieldAPIName: undefined,
                    moduleAPIName: undefined,
                  })
                    .then((data) => {
                      const newFile = structuredClone(file);
                      const transcript = data?.data?.data?.Transcript;

                      newFile.uploading = false;

                      if (transcript) {
                        newFile.uploadedFileData.Transcript = transcript;
                      } else {
                        newFile.uploadedFileData.Index = true;
                      }

                      setFiles((prev) => {
                        return prev.map((f) => {
                          if (f.uploadID === newFile.uploadID) {
                            return newFile;
                          }

                          return f;
                        });
                      });
                    })
                    .catch((err) => {
                      const errorMessage = getErrorMessage(err);

                      const fileIds = finalFileArr.map((f) => f.uploadID);

                      setFiles((prev) =>
                        prev.filter((f) => !fileIds.includes(f.uploadID))
                      );

                      if (errorMessage === "File index is not built.") {
                        FileIndexNeeded(navigate, tenantID!);
                      } else {
                        newToastEvent("error", getErrorMessage(err));
                      }
                    });
                }
              }}
              fileType={undefined}
              fileTypes={Object.values(SUPPORTED_EXTENSIONS).flatMap((v) => v)}
              accept={Object.keys(SUPPORTED_EXTENSIONS)}
              plugins={["Audio"]}
              setKeepOpen={setKeepOpen}
              multiFiles
              filesToFilter={(files || []).map((f) => ({
                ...f.uploadedFileData,
              }))}
            />
          </Popover>
        }
      >
        <Button
          outline
          color="primary"
          size="sm"
          className="tw:flex tw:items-center tw:h-[39px] tw:rounded-lg"
        >
          <div className="tw:transform tw:rotate-270 tw:pl-1 tw:mt-1">
            <i className="mdi mdi-attachment tw:text-2xl" />
          </div>
          <span className="tw:hidden tw:lg:block">Add File</span>
        </Button>
      </Whisper>
    </div>
  );
}

export function FileIndexNeeded(navigate: NavigateFunction, tenantID: string) {
  AppConfirm({
    title: "File Index Not Built",
    text: `Your cloud does not yet have the File Index required for chatting with files. Enable "Chat With Files" in your cloud settings to enable this feature.`,
    confirmButtonText: "Go to Settings",
    confirmButtonColor: "var(--primary)",
  }).then((result) => {
    if (!result.isConfirmed) return;

    navigate(`/clouds/${tenantID}/settings/globals/Settings`);
  });
}
